import React, { ReactNode, useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { mergeClassNames } from '../../../utils';

interface StyledAccordionProps {
  header: ReactNode;
  children: ReactNode;
  defaultExpanded?: boolean; // Optional prop to control initial expanded state
  summaryBackground?: string; // Optional prop to update background color
  accordionSummaryRootClass?: string; // Optional prop to add CSS class to accordion summary
}

const useStyles = makeStyles({
  accordionRoot: {
    borderRadius: '0 !important',
    borderBottom: '1px solid var(--grey-02)',
    boxShadow: 'none',
    margin: '0 !important',
    '&:before': {
      display: 'none',
    },
  },
  accordionSummaryRoot: (props: { summaryBackground: string }) => ({
    backgroundColor: props.summaryBackground,
    borderBottom: '1px solid var(--grey-02)',
  }),
  accordionSummaryContent: {
    margin: '1rem 0 !important',
    '@media (min-width: 769px)': {
      margin: '2rem 0 !important',
    },
  },
  accordionSummaryIconButton: {
    fontSize: 'var(--font-size-24)',
    fill: 'var(--black-02)',
    '@media (min-width: 769px)': {
      fontSize: 'var(--font-size-30)',
    },
  },
  accordionDetailsRoot: {
    padding: '0',
    display: 'block',
  },
});

const StyledAccordion: React.FC<StyledAccordionProps> = ({
  header,
  children,
  defaultExpanded = false,
  summaryBackground = 'var(--grey-01)',
  accordionSummaryRootClass,
}) => {
  const classes = useStyles({ summaryBackground });
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Update state if defaultExpanded prop changes
  useEffect(() => {
    setIsExpanded(defaultExpanded);
  }, [defaultExpanded]);

  const handleExpandChange = (newExpandedState: boolean) => {
    setIsExpanded(newExpandedState);
  };

  return (
    <Accordion
      expanded={isExpanded}
      onChange={(_, expanded) => handleExpandChange(expanded)}
      classes={{ root: classes.accordionRoot }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon className={classes.accordionSummaryIconButton} />}
        classes={{
          root: mergeClassNames(classes.accordionSummaryRoot, accordionSummaryRootClass),
          content: classes.accordionSummaryContent,
        }}
        onClick={() => handleExpandChange(!isExpanded)}
      >
        {header}
      </AccordionSummary>
      <AccordionDetails classes={{ root: classes.accordionDetailsRoot }}>{children}</AccordionDetails>
    </Accordion>
  );
};

export default StyledAccordion;
